import {
  cellReader,
  rowNumberOf,
  summarizeRows,
  suggestMapping,
  type ColumnMappingOf,
  type ImportColumnDef,
  type ImportIssueOf,
  type ImportRowStatus,
  type ImportSummary,
} from '../imports/table-mapping';
import { normalizeName } from '../shared/naming';
import type { ProjectId } from '../shared/ids';
import { activityNaturalKey, type Activity } from './activity';
import { isAvailableForProject, type Discipline } from './discipline';
import { floorNaturalKey, type Floor } from './floor';
import { towerNaturalKey, type Tower } from './tower';

/**
 * Importação da estrutura da obra por planilha.
 *
 * ```text
 * OBRA → TORRE → PAVIMENTO → DISCIPLINA(ATIVIDADE)
 * ```
 *
 * Uma linha da planilha descreve **um caminho inteiro** nessa hierarquia: "Torre A, 3º
 * Pavimento, nível 3, Alvenaria". Torre e pavimento repetidos entre linhas são o mesmo
 * registro — é assim que a planilha do escritório se parece, com a torre repetida em
 * dezenas de linhas, e obrigar uma aba por nível seria pedir que ela fosse reescrita.
 *
 * Tudo aqui é **puro**: recebe as linhas já lidas e devolve o que aconteceria. Nada toca
 * o banco antes da confirmação (RN-030).
 *
 * A disciplina fora do catálogo é recusada **por padrão**, com o nome escrito na tela: um
 * "Alvenaira" digitado errado viraria disciplina permanente. Quem confirma que a planilha
 * é a fonte liga `createMissingDisciplines`, e aí ela nasce **na obra**, nunca no catálogo
 * global (RN-007) — o erro de digitação fica contido em uma obra em vez de aparecer em
 * todas.
 */

export type StructureImportField = 'tower' | 'floor' | 'level' | 'discipline';

export const STRUCTURE_IMPORT_COLUMNS: readonly ImportColumnDef<StructureImportField>[] = [
  {
    field: 'tower',
    label: 'Torre',
    required: true,
    synonyms: ['torre', 'bloco', 'edificio', 'predio', 'unidade', 'empreendimento'],
  },
  {
    field: 'floor',
    label: 'Pavimento',
    required: true,
    synonyms: ['pavimento', 'andar', 'piso', 'nivel do pavimento', 'pav'],
  },
  {
    field: 'level',
    label: 'Nível',
    required: false,
    synonyms: ['nivel', 'cota', 'ordem', 'sequencia', 'numero do andar'],
  },
  {
    field: 'discipline',
    label: 'Disciplina',
    required: true,
    synonyms: ['disciplina', 'servico', 'etapa', 'atividade', 'frente', 'especialidade'],
  },
];

export type StructureColumnMapping = ColumnMappingOf<StructureImportField>;
export type StructureImportIssue = ImportIssueOf<StructureImportField>;

export function suggestStructureMapping(headers: readonly string[]): StructureColumnMapping {
  return suggestMapping(headers, STRUCTURE_IMPORT_COLUMNS);
}

export interface StructureImportRow {
  readonly rowNumber: number;
  readonly status: ImportRowStatus;
  readonly towerName: string;
  readonly floorName: string;
  /** Ausente na planilha = deduzido da ordem de aparição do pavimento na torre. */
  readonly level: number;
  readonly levelFromFile: boolean;
  readonly disciplineName: string;
  readonly disciplineId: string | undefined;
  /** Torre existente; ausente = seria criada. */
  readonly towerId: string | undefined;
  readonly floorId: string | undefined;
  /** Atividade já existente para o par pavimento × disciplina (RN-004). */
  readonly activityId: string | undefined;
  readonly issues: readonly StructureImportIssue[];
}

export interface ClassifyStructureImportInput {
  readonly rows: readonly Record<string, string>[];
  readonly mapping: StructureColumnMapping;
  readonly projectId: ProjectId;
  readonly towers: readonly Tower[];
  readonly floors: readonly Floor[];
  readonly activities: readonly Activity[];
  readonly disciplines: readonly Discipline[];
  /**
   * Criar as disciplinas que a planilha traz e o catálogo não tem — **na obra**.
   *
   * Desligado, a linha é recusada e o import vira um beco sem saída: sair da tela,
   * cadastrar a disciplina e escolher o arquivo de novo. Ligado, o caminho se fecha aqui.
   */
  readonly createMissingDisciplines?: boolean;
}

export interface StructureImportPlan {
  readonly rows: readonly StructureImportRow[];
  readonly summary: ImportSummary;
  readonly newTowers: readonly string[];
  /** `"Torre A · 3º Pavimento"` — os pavimentos que nasceriam. */
  readonly newFloors: readonly string[];
  readonly unknownDisciplines: readonly string[];
}

/**
 * Classifica cada linha do arquivo.
 *
 * - torre, pavimento ou disciplina vazios → `INVALID`; são o caminho inteiro da atividade;
 * - disciplina fora do catálogo → `INVALID`, com o nome escrito — a menos que se peça
 *   para criá-la, e aí ela entra como disciplina da obra;
 * - nível não numérico → `INVALID`; ordenar pavimento por texto colocaria o 10º antes do 2º;
 * - mesmo par pavimento × disciplina repetido **no arquivo** → `DUPLICATE` (RN-004);
 * - atividade que já existe → `UNCHANGED`; a estrutura não tem o que atualizar aqui;
 * - o resto → `CREATE`, criando torre e pavimento junto quando faltarem.
 */
export function classifyStructureImport(input: ClassifyStructureImportInput): StructureImportPlan {
  const towerByKey = new Map(
    input.towers.map((tower) => [towerNaturalKey(input.projectId, tower.name), tower]),
  );
  const floorByKey = new Map(
    input.floors.map((floor) => [floorNaturalKey(floor.towerId, floor.name), floor]),
  );
  const disciplineByKey = new Map(
    input.disciplines
      .filter((discipline) => isAvailableForProject(discipline, input.projectId))
      .map((discipline) => [normalizeName(discipline.name), discipline]),
  );
  const activityByKey = new Map(
    input.activities.map((activity) => [
      activityNaturalKey(activity.floorId, activity.disciplineId),
      activity,
    ]),
  );

  const newTowers = new Map<string, string>();
  const newFloors = new Map<string, string>();
  const unknownDisciplines = new Set<string>();
  const seenPairs = new Set<string>();
  // Nível deduzido: o pavimento novo entra na ordem em que aparece dentro da torre.
  const nextLevel = new Map<string, number>();
  const rows: StructureImportRow[] = [];

  input.rows.forEach((raw, index) => {
    const pick = cellReader(raw, input.mapping);
    const issues: StructureImportIssue[] = [];

    const towerName = pick('tower');
    const floorName = pick('floor');
    const levelRaw = pick('level');
    const disciplineName = pick('discipline');

    if (towerName === '') issues.push({ field: 'tower', message: 'Torre vazia.' });
    if (floorName === '') issues.push({ field: 'floor', message: 'Pavimento vazio.' });
    if (disciplineName === '') issues.push({ field: 'discipline', message: 'Disciplina vazia.' });

    const discipline =
      disciplineName === '' ? undefined : disciplineByKey.get(normalizeName(disciplineName));
    if (disciplineName !== '' && discipline === undefined) {
      unknownDisciplines.add(disciplineName);
      if (input.createMissingDisciplines !== true) {
        issues.push({
          field: 'discipline',
          message: `Disciplina "${disciplineName}" não existe. Marque para criar as que faltam.`,
        });
      }
    }

    const towerKey = towerNaturalKey(input.projectId, towerName);
    const tower = towerName === '' ? undefined : towerByKey.get(towerKey);
    if (towerName !== '' && tower === undefined) newTowers.set(towerKey, towerName);

    // Sem torre gravada ainda, o pavimento é identificado pela dupla torre + nome.
    const floorKey =
      tower === undefined
        ? `${towerKey}|${normalizeName(floorName)}`
        : floorNaturalKey(tower.id, floorName);
    const floor = floorName === '' ? undefined : floorByKey.get(floorKey);

    let level: number;
    let levelFromFile = false;
    if (levelRaw === '') {
      if (floor !== undefined) {
        level = floor.level;
      } else {
        const current = nextLevel.get(towerKey) ?? 0;
        level = current;
        nextLevel.set(towerKey, current + 1);
      }
    } else {
      const parsed = Number(levelRaw.replace(',', '.'));
      if (!Number.isInteger(parsed)) {
        issues.push({ field: 'level', message: `Nível "${levelRaw}" não é um número inteiro.` });
        level = 0;
      } else {
        level = parsed;
        levelFromFile = true;
      }
    }

    if (floorName !== '' && floor === undefined && towerName !== '') {
      newFloors.set(floorKey, `${towerName} · ${floorName}`);
    }

    const pairKey = `${floorKey}|${normalizeName(disciplineName)}`;
    const repeated = seenPairs.has(pairKey);
    if (disciplineName !== '' && floorName !== '') seenPairs.add(pairKey);

    const activity =
      floor === undefined || discipline === undefined
        ? undefined
        : activityByKey.get(activityNaturalKey(floor.id, discipline.id));

    let status: ImportRowStatus;
    if (issues.length > 0) {
      status = 'INVALID';
    } else if (repeated) {
      status = 'DUPLICATE';
      issues.push({ field: 'row', message: 'Mesmo pavimento e disciplina repetidos no arquivo.' });
    } else if (activity !== undefined) {
      status = 'UNCHANGED';
    } else {
      status = 'CREATE';
    }

    rows.push({
      rowNumber: rowNumberOf(index),
      status,
      towerName,
      floorName,
      level,
      levelFromFile,
      disciplineName,
      disciplineId: discipline?.id,
      towerId: tower?.id,
      floorId: floor?.id,
      activityId: activity?.id,
      issues,
    });
  });

  return {
    rows,
    summary: summarizeRows(rows),
    newTowers: [...newTowers.values()],
    newFloors: [...newFloors.values()],
    unknownDisciplines: [...unknownDisciplines],
  };
}
